import { gql, useLazyQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';

import { ConvertInvoiceToRaContext } from '../contexts';
import { useConvertInvoiceToRaAlert } from '../hooks/use-convert-invoice-to-ra-alert';
import { useConvertInvoiceToRaMutation } from '../hooks/use-convert-invoice-to-ra-mutation';
import { ConvertInvoiceToRaProviderProps } from '../types';

type GetSourceBzObjectResponse = {
  getSourceBzObject: { id: number; value: number; paidAmount: number } | null;
};

type GetSourceBzObjectVars = {
  bzObjType: BzObjType;
  bzObjMemberCode: number;
};

const SOURCE_BZ_OBJECT_FIELDS = `
  id: code
  value: wert
  paidAmount,
`;

const GET_SOURCE_BZ_OBJECT = gql`
  query GetSourceBzObject($bzObjType: BzObjType!, $bzObjMemberCode: Int!) {
    getSourceBzObject(
      bzObjType: $bzObjType
      bzObjMemberCode: $bzObjMemberCode
    ) {
      ... on Rechnung {
        ${SOURCE_BZ_OBJECT_FIELDS}
      }
    }
  }
`;

export const ConvertInvoiceToRaProvider = ({
  children,
  invoiceId,
  invoiceValue,
  reportId,
}: ConvertInvoiceToRaProviderProps) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(false);

  const toggleAbility = useCallback(() => {
    setIsEnabled(!isEnabled);
  }, [isEnabled]);

  const convertInvoiceToRa = useConvertInvoiceToRaMutation({
    invoiceId,
    reportId,
  });
  const alert = useConvertInvoiceToRaAlert();

  const dialogs = useDialogs();

  const [loadSourceBzObject] = useLazyQuery<
    GetSourceBzObjectResponse,
    GetSourceBzObjectVars
  >(GET_SOURCE_BZ_OBJECT, { fetchPolicy: 'network-only' });

  // This is process of quick checkout for Credit Note '
  // Credit Note is an invoice
  const convert = useCallback(async () => {
    const { data: source } = await loadSourceBzObject({
      variables: {
        bzObjType: BzObjType.RECHNUNG,
        bzObjMemberCode: invoiceId,
      },
    });
    const originalInvoiceValue = source.getSourceBzObject?.value;
    const paidAmount = source.getSourceBzObject?.paidAmount;
    const sumIsZero = originalInvoiceValue + invoiceValue;
    let confirmed = false;
    if (sumIsZero === 0 && paidAmount === 0) {
      confirmed = await dialogs.confirm({
        title: t('COMMON.INFO'),
        description: t('ALERTS.INVOICE_DEBIT_PAYMENT_CONFIRMATION'),
        confirmLabel: t('COMMON.YES'),
        cancelLabel: t('COMMON.NO'),
      });
    }
    //confirmed
    return await convertInvoiceToRa(confirmed);
  }, [
    convertInvoiceToRa,
    dialogs,
    invoiceId,
    invoiceValue,
    loadSourceBzObject,
    t,
  ]);

  return (
    <ConvertInvoiceToRaContext.Provider
      value={{ isEnabled, toggleAbility, convert, alert, reportId }}
    >
      {children}
    </ConvertInvoiceToRaContext.Provider>
  );
};
